/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */

// redux utils
import {createSlice, current} from '@reduxjs/toolkit';
// thunks

import {
    createReport,
    deleteReport,
    downloadReport,
    fetchAllReportsByAccount,
    fetchReport,
    hydrateReport,
    updateReport,
    getPreviewReport,
} from "../thunks/report";
import {
    METRICS,
    DEFAULT_FORM_VALUES,
    IS_SCHEDULED,
    DIMENSIONS,
    DIMENSIONS_OPTIONS,
    TOTAL_METRICS_OPTIONS, STANDARD_DIMENSION, INTERVAL_DIMENSION
} from "../../pages/dashboard/reportbuilder/monetizationform/consts";
import {
    MONETIZATION_METRICS,
    PERFORMANCE_METRICS,
    PREBID_MONETIZATION_METRICS,
    PREBID_VIDEO_METRICS
} from "../../pages/dashboard/reportbuilder/monetizationform/consts";

/* ========== ~~~~~~~~~~ ACCOUNT SLICE : REDUCER ~~~~~~~~~~ ========== */
const initialState = {
    // all reports per account
    allReports: [],
    newReport: {...DEFAULT_FORM_VALUES},
    // network reqs
    allReportRequestStatus: 'idle',
    reportRequestStatus: 'idle',
    downloadStatus: {},
    previewData: [],
    previewRequestStatus: 'idle',
    previewDownloadStatus: 'idle',
    allReportsReceived: false,
    reportReceived: false,
    reportUpdated: false,
    reportDeleted: false,
    reportCreated: false,
    reportPreviewVisited: false,
    error: '',
    isPreviewing: false
};

// Build a report's editable form state (splits dimensions/metrics into the UI buckets). Shared by
// initReportForm and hydrateReport; null-safe for a not-yet-hydrated report.
const buildFormState = (state, uuid, sitesAndAppsUUIDs) => {
    const report = state.allReports.find(r => r.reportUUID === uuid)
    if (!report) return
    state[uuid] = {...report}
    // if all sites selected, populate from account's full site list
    if (report.allSitesSelected && sitesAndAppsUUIDs) {
        state[uuid].filters = {
            ...state[uuid].filters,
            SITE: [...Object.keys(sitesAndAppsUUIDs)]
        }
    }
    const allDimensions = state[uuid][DIMENSIONS] || [];
    const allMetrics = state[uuid][METRICS] || [];
    const standardDimensionsOptions = DIMENSIONS_OPTIONS.filter(o => o.type === STANDARD_DIMENSION).map(o => o.value);
    const intervalDimensionsOptions = DIMENSIONS_OPTIONS.filter(o => o.type === INTERVAL_DIMENSION).map(o => o.value);
    const performanceMetrics = TOTAL_METRICS_OPTIONS.filter(o => o.type === PERFORMANCE_METRICS).map(o => o.value);
    const monetizationMetrics = TOTAL_METRICS_OPTIONS.filter(o => o.type === MONETIZATION_METRICS).map(o => o.value);
    const prebidMonetizationMetricsOptions = TOTAL_METRICS_OPTIONS.filter(o => o.type === PREBID_MONETIZATION_METRICS).map(o => o.value);
    const prebidVideoMetricsOptions = TOTAL_METRICS_OPTIONS.filter(o => o.type === PREBID_VIDEO_METRICS).map(o => o.value);
    state[uuid][STANDARD_DIMENSION] = allDimensions.filter(m => standardDimensionsOptions.includes(m))
    state[uuid][INTERVAL_DIMENSION] = allDimensions.filter(m => intervalDimensionsOptions.includes(m))
    state[uuid][PREBID_VIDEO_METRICS] =  allMetrics.filter(m => prebidVideoMetricsOptions.includes(m))
    state[uuid][PREBID_MONETIZATION_METRICS] =  allMetrics.filter(m => prebidMonetizationMetricsOptions.includes(m))
    state[uuid][PERFORMANCE_METRICS] =  allMetrics.filter(m => performanceMetrics.includes(m))
    state[uuid][MONETIZATION_METRICS] =  allMetrics.filter(m => monetizationMetrics.includes(m))
    state[uuid].isOpened = false
}

export const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        initReportForm: (state, action) => {
            const uuid = action.payload?.uuid || action.payload;
            const sitesAndAppsUUIDs = action.payload?.sitesAndAppsUUIDs;
            if (state.allReports.length && !state[uuid]) {
                buildFormState(state, uuid, sitesAndAppsUUIDs)
            }
        },
        updateReportForm: (state, action) => {
            const {reportUUID} = action.payload // reportUUID
            if (state[reportUUID]) {
                state[reportUUID] = action.payload
            }
        },
        toggleIsOpened: (state, action) => {
            if (state[action.payload]) {
                state[action.payload].isOpened = !state[action.payload].isOpened
            }
        },
        resetDownloadStatus: (state, action) => {
            state.downloadStatus[action.payload] = 'idle'
        },
        resetPreviewDownloadStatus: (state, action) => {
            state.previewDownloadStatus = 'idle'
        },
        setIsPreviewing: (state, action) => {
            state.isPreviewing = action.payload
        },
        setPreviewVisited: (state, action) => {
            state.reportPreviewVisited = action.payload
        },
        refresh: () => initialState
    },
    extraReducers: builder => {
        builder
            .addCase(fetchAllReportsByAccount.pending, (state, action) => {
                state.allReportsReceived = false
                state.reportRequestStatus = 'pending'
            })
            .addCase(fetchAllReportsByAccount.fulfilled, (state, action) => {
                state.allReports = action.payload.map(r => {
                    const isScheduled =
                        r.schedule && r.schedule.startDate && r.schedule.endDate &&
                        r.schedule.format && r.schedule.recipientEmails.length > 0
                    r[IS_SCHEDULED] = isScheduled;
                    return r;
                })
                state.allReportsReceived = true
                state.reportDeleted = false
                state.reportCreated = false
                state.reportRequestStatus = 'idle'
            })
            .addCase(fetchAllReportsByAccount.rejected, (state, rejectedValue) => {
                state.error = rejectedValue.error.message
                state.allReportsReceived = false
                state.reportRequestStatus = 'error'
            })
            .addCase(createReport.pending, (state, action) => {
                state.reportRequestStatus = 'pending'
                state.reportCreated = false
            })
            .addCase(createReport.fulfilled, (state, action) => {
                state.reportCreated = true
                state.reportRequestStatus = 'idle'
                state.newReport = {...DEFAULT_FORM_VALUES}
            })
            .addCase(createReport.rejected, (state, rejectedValue) => {
                state.error = rejectedValue.error.message
                state.reportCreated = false
                state.reportRequestStatus = 'error'
            })
            .addCase(fetchReport.pending, (state, action) => {
                state.reportReceived = false
                state.reportRequestStatus = 'pending'
            })
            .addCase(fetchReport.fulfilled, (state, action) => {
                state.report = {...action.payload}
                state.reportReceived = true
                state.reportRequestStatus = 'idle'
            })
            .addCase(hydrateReport.fulfilled, (state, action) => {
                // Full config for a lazily loaded SRMS report arrived: swap in the full list entry
                // (keep createdAt) and rebuild the open form, preserving expansion.
                const { report, sitesAndAppsUUIDs } = action.payload;
                const uuid = report.reportUUID;
                const idx = state.allReports.findIndex(r => r.reportUUID === uuid);
                if (idx >= 0) {
                    state.allReports[idx] = { ...report, createdAt: state.allReports[idx].createdAt };
                }
                const wasOpened = state[uuid]?.isOpened ?? true;
                delete state[uuid];
                buildFormState(state, uuid, sitesAndAppsUUIDs);
                if (state[uuid]) state[uuid].isOpened = wasOpened;
            })
            .addCase(updateReport.pending, (state, action) => {
                state.allReportsReceived = false
                state.reportUpdated = false
                state.reportRequestStatus = 'pending'
            })
            .addCase(updateReport.fulfilled, (state, action) => {
                state.reportUpdated = true
                state.reportRequestStatus = 'idle'
            })
            .addCase(updateReport.rejected, (state, rejectedValue) => {
                state.error = rejectedValue.error.message
                state.reportUpdated = false
                state.reportRequestStatus = 'error'
            })
            .addCase(deleteReport.pending, (state, action) => {
                state.allReportsReceived = false
                state.reportDeleted = false
                state.reportRequestStatus = 'pending'
            })
            .addCase(deleteReport.fulfilled, (state, action) => {
                const uuid = action.meta?.arg;
                state.reportDeleted = true
                state.reportRequestStatus = 'idle'
                if (state[uuid]) delete state[uuid]
                state.allReports = state.allReports.filter(r => r.reportUUID !== uuid)
            })
            .addCase(deleteReport.rejected, (state, rejectedValue) => {
                state.error = rejectedValue.error.message
                state.reportDeleted = false
                state.reportRequestStatus = 'error'
            })
            .addCase(downloadReport.pending, (state, action) => {
                const uuid = action.meta?.arg;
                state.downloadStatus[uuid] = 'pending'
            })
            .addCase(downloadReport.fulfilled, (state, action) => {
                const uuid = action.meta?.arg;
                const payload = action.payload || {};
                if (payload.processing) {
                    // PPA: still generating
                    state.downloadStatus[uuid] = 'processing';
                } else if (payload.blob) {
                    // SRMS: file returned directly — save client-side
                    state.downloadStatus[uuid] = 'success'
                    const objectUrl = URL.createObjectURL(payload.blob);
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.download = payload.filename || 'report.csv';
                    link.click();
                    URL.revokeObjectURL(objectUrl);
                } else {
                    // PPA: redirect to the signed URL
                    state.downloadStatus[uuid] = 'success'
                    window.location.href = payload.url;
                }
            })
            .addCase(downloadReport.rejected, (state, rejectedValue) => {
                const uuid = rejectedValue.meta?.arg;
                state.error = rejectedValue.error.message
                state.downloadStatus[uuid] = 'error'
            })
            .addCase(getPreviewReport.pending, (state, action) => {
                const format = action.meta?.arg.schedule?.format
                if (format === 'JSON') {
                    state.previewRequestStatus = 'pending'
                } else if (format === 'CSV') {
                    state.previewDownloadStatus = 'pending'
                }
            })
            .addCase(getPreviewReport.fulfilled, (state, action) => {
                const format = action.meta?.arg.schedule?.format
                if (format === 'JSON') {
                    state.previewRequestStatus = 'success'
                    state.previewData = action.payload
                } else if (format === 'CSV' || format === 'XLSX') {
                    state.previewDownloadStatus = 'success'
                    const blob = URL.createObjectURL(action.payload.data);
                    const link = document.createElement('a');
                    link.href = blob;
                    link.download = action.payload.filename;
                    link.click()
                }
            })
            .addCase(getPreviewReport.rejected, (state, action) => {
                const format = action.meta?.arg.schedule?.format
                if (format === 'JSON') {
                    state.previewRequestStatus = 'error'
                } else if (format === 'CSV') {
                    state.previewDownloadStatus = 'error'
                }
                state.error = action.error.message
            })
    }
});

export const { toggleIsOpened, initReportForm, updateReportForm, refresh, resetDownloadStatus, resetPreviewDownloadStatus, setIsPreviewing,setPreviewVisited} = reportSlice.actions
export default reportSlice.reducer;
